import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import API, { getImagenUrl } from 'shared/api';
import Spinner from 'shared/components/Spinner';
import {
  FiBox, FiPlus, FiTrash2, FiMapPin, FiArrowRight, FiCheck, FiX,
  FiLayers, FiPackage, FiHome, FiShoppingCart, FiSearch, FiEdit3, FiEyeOff,
  FiEdit2,
} from 'react-icons/fi';

const num = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('es-VE', { maximumFractionDigits: 3 });
};

export default function DepositosTab() {
  const { isAdmin, tienePermiso } = useAuth();
  const { COL } = useDarkMode();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const tp = (c) => tienePermiso(c);
  const puedeGestionar = isAdmin() || tp('depositos.gestionar');
  const puedeTransferir = isAdmin() || tp('depositos.transferir');
  const vStock = tp('vis.depositos.stock');
  const vUbicacion = tp('vis.depositos.ubicacion');

  const [sedes, setSedes] = useState([]);
  const [sede, setSede] = useState('');
  const [depositos, setDepositos] = useState([]);
  const [depSel, setDepSel] = useState(null);
  const [depoVenta, setDepoVenta] = useState(null);
  const [cargando, setCargando] = useState(false);

  // Detalle del depósito seleccionado
  const [detalle, setDetalle] = useState({ productos: [], insumos: [], ubicaciones: [] });
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [vista, setVista] = useState('productos');
  const [q, setQ] = useState('');

  // Modales
  const [modalDeposito, setModalDeposito] = useState(null); // { editar? } | null
  const [modalUbicacion, setModalUbicacion] = useState(null);
  const [modalTransferir, setModalTransferir] = useState(null);
  const [modalUbicar, setModalUbicar] = useState(null); // { tipo: 'productos' | 'insumos' } | null
  const [guardando, setGuardando] = useState(false);

  const loadSedes = useCallback(async () => {
    try {
      const r = await API.get('/sedes/listar');
      const list = r.data?.data || [];
      setSedes(list);
      const activa = list.find((s) => s.activa) || list[0];
      if (activa) setSede((prev) => prev || activa.codigo);
    } catch (e) {
      console.error('Error cargando sedes', e);
    }
  }, []);

  const loadDepositos = useCallback(async (sedeCodigo) => {
    if (!sedeCodigo) { setDepositos([]); return; }
    setCargando(true);
    try {
      const r = await API.get('/depositos/listar', { params: { sede: sedeCodigo } });
      setDepositos(r.data?.data || []);
    } catch (e) {
      console.error('Error cargando depósitos', e);
    } finally {
      setCargando(false);
    }
  }, []);

  const loadDepoVenta = useCallback(async (sedeCodigo) => {
    if (!sedeCodigo) { setDepoVenta(null); return; }
    try {
      const r = await API.get(`/depositos/sede/${sedeCodigo}/deposito-venta`);
      setDepoVenta(r.data?.data || null);
    } catch (e) {
      setDepoVenta(null);
    }
  }, []);

  const loadDetalle = useCallback(async (depositoId) => {
    if (!depositoId) { setDetalle({ productos: [], insumos: [], ubicaciones: [] }); return; }
    setCargandoDetalle(true);
    try {
      const [u, p, i] = await Promise.all([
        API.get('/depositos/ubicaciones/listar', { params: { deposito_id: depositoId } }),
        API.get(`/depositos/${depositoId}/productos`),
        API.get(`/depositos/${depositoId}/insumos`),
      ]);
      setDetalle({
        ubicaciones: u.data?.data || [],
        productos: p.data?.data || [],
        insumos: i.data?.data || [],
      });
    } catch (e) {
      console.error('Error cargando detalle', e);
    } finally {
      setCargandoDetalle(false);
    }
  }, []);

  useEffect(() => { loadSedes(); }, [loadSedes]);
  useEffect(() => { if (sede) { loadDepositos(sede); loadDepoVenta(sede); } }, [sede, loadDepositos, loadDepoVenta]);
  useEffect(() => { loadDetalle(depSel); }, [depSel, loadDetalle]);

  // Si cambia la sede, limpiar selección
  const cambiarSede = (s) => { setSede(s); setDepSel(null); setVista('productos'); setQ(''); };

  const guardarDeposito = async (nombre, editarId) => {
    if (!nombre.trim() || !sede) return;
    setGuardando(true);
    try {
      if (editarId) {
        await API.put(`/depositos/${editarId}`, { nombre });
      } else {
        await API.post('/depositos', { sede_codigo: sede, nombre });
      }
      setModalDeposito(null);
      await loadDepositos(sede);
    } catch (e) {
      console.error(e);
    } finally {
      setGuardando(false);
    }
  };

  const toggleDeposito = async (id) => {
    try {
      await API.post(`/depositos/${id}/toggle`);
      loadDepositos(sede);
    } catch (e) { console.error(e); }
  };

  const eliminarDeposito = async (id) => {
    if (!window.confirm('¿Eliminar este depósito? Se perderá su stock registrado.')) return;
    try {
      await API.delete(`/depositos/${id}`);
      if (depSel === id) setDepSel(null);
      loadDepositos(sede);
    } catch (e) { console.error(e); }
  };

  const guardarUbicacion = async (datos) => {
    if (!depSel || !datos.nombre.trim()) return;
    setGuardando(true);
    try {
      if (datos.id) {
        await API.put(`/depositos/ubicaciones/${datos.id}`, { nombre: datos.nombre });
      } else {
        await API.post('/depositos/ubicaciones', { deposito_id: depSel, nombre: datos.nombre });
      }
      setModalUbicacion(null);
      loadDetalle(depSel);
    } catch (e) { console.error(e); } finally { setGuardando(false); }
  };

  const eliminarUbicacion = async (id) => {
    if (!window.confirm('¿Eliminar esta ubicación?')) return;
    try {
      await API.delete(`/depositos/ubicaciones/${id}`);
      loadDetalle(depSel);
    } catch (e) { console.error(e); }
  };

  const ubicarProducto = async (codigo, ubicacionId) => {
    if (!depSel) return;
    try {
      await API.put(`/depositos/producto/${encodeURIComponent(codigo)}/ubicacion`, { deposito_id: depSel, ubicacion_id: ubicacionId || null });
      loadDetalle(depSel);
    } catch (e) { console.error(e); }
  };

  const ubicarProductos = async (ubicacionId, codigos) => {
    if (!depSel || !codigos.length) return;
    setGuardando(true);
    try {
      await API.put('/depositos/productos/ubicacion', { deposito_id: depSel, ubicacion_id: ubicacionId, codigos });
      setModalUbicar(null);
      loadDetalle(depSel);
    } catch (e) {
      alert(e.response?.data?.detail || 'No se pudo asignar la ubicación');
    } finally { setGuardando(false); }
  };

  const ubicarInsumos = async (ubicacionId, insumoIds) => {
    if (!depSel || !insumoIds.length) return;
    setGuardando(true);
    try {
      await API.put('/depositos/insumos/ubicacion', { deposito_id: depSel, ubicacion_id: ubicacionId, insumo_ids: insumoIds });
      setModalUbicar(null);
      loadDetalle(depSel);
    } catch (e) {
      alert(e.response?.data?.detail || 'No se pudo asignar la ubicación');
    } finally { setGuardando(false); }
  };

  const ubicarInsumo = async (insumoId, ubicacionId) => {
    if (!depSel) return;
    try {
      await API.put(`/depositos/insumo/${insumoId}/ubicacion`, { deposito_id: depSel, ubicacion_id: ubicacionId || null });
      loadDetalle(depSel);
    } catch (e) { console.error(e); }
  };

  const setDepositoVenta = async (depositoId) => {
    if (!sede) return;
    try {
      await API.put(`/depositos/sede/${sede}/deposito-venta`, { deposito_id: depositoId });
      loadDepoVenta(sede);
    } catch (e) { console.error(e); }
  };

  const transferir = async (destino, items) => {
    if (!destino) { alert('Selecciona el depósito destino'); return; }
    if (!items.length) { alert('Agrega al menos un producto para transferir'); return; }
    setGuardando(true);
    let ok = 0;
    const errores = [];
    for (const it of items) {
      try {
        await API.post('/depositos/transferir', {
          tipo: 'producto',
          deposito_origen: depSel,
          deposito_destino: destino,
          codigo_producto: it.codigo,
          cantidad: it.cantidad,
        });
        ok += 1;
      } catch (e) {
        errores.push(`${it.nombre || it.codigo}: ${e.response?.data?.detail || 'error'}`);
      }
    }
    setGuardando(false);
    setModalTransferir(null);
    loadDetalle(depSel);
    if (errores.length) {
      alert(`${ok} transferido(s).\n${errores.join('\n')}`);
    }
  };

  const depSelInfo = depositos.find((d) => d.id === depSel) || null;

  const productosFiltrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return detalle.productos;
    return detalle.productos.filter((p) =>
      (p.nombre || '').toLowerCase().includes(t) || (p.codigo || '').toLowerCase().includes(t)
    );
  }, [detalle.productos, q]);

  const insumosFiltrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return detalle.insumos;
    return detalle.insumos.filter((i) =>
      (i.nombre || '').toLowerCase().includes(t) || (i.codigo || '').toLowerCase().includes(t)
    );
  }, [detalle.insumos, q]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Encabezado + selector de sede */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COL.text }}>Depósitos y ubicaciones</div>
          <div style={{ fontSize: 12.5, color: COL.muted, marginTop: 2, maxWidth: 520 }}>
            Organiza el stock de cada sede en depósitos y ubica cada producto o insumo.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {sedes.length > 0 && (
            <select
              value={sede}
              onChange={(e) => cambiarSede(e.target.value)}
              style={{ padding: '9px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13, cursor: 'pointer', maxWidth: 220 }}
            >
              {sedes.map((s) => (
                <option key={s.codigo} value={s.codigo}>{s.nombre || s.codigo}</option>
              ))}
            </select>
          )}
          {puedeGestionar && sede && (
            <Boton COL={COL} onClick={() => setModalDeposito({ editar: null })}>
              <FiPlus size={14} /> Nuevo depósito
            </Boton>
          )}
        </div>
      </div>

      {!sede ? (
        <EmptyPanel COL={COL} icon={FiMapPin} title="Selecciona una sede" desc="Elige una sede para ver y organizar sus depósitos." />
      ) : cargando ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner />
        </div>
      ) : depositos.length === 0 ? (
        <EmptyPanel
          COL={COL}
          icon={FiBox}
          title="Aún no hay depósitos"
          desc="Cada sede tiene un depósito principal que se crea automáticamente con su stock actual. Puedes crear depósitos adicionales (ej. Almacén, Cava) para organizar mejor tu inventario."
          action={puedeGestionar ? { label: 'Crear depósito', fn: () => setModalDeposito({ editar: null }) } : null}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '300px 1fr', gap: 14, alignItems: 'start' }}>
          {/* Lista de depósitos */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {depositos.map((d) => {
              const activo = depSel === d.id;
              const esVenta = depoVenta?.id === d.id;
              return (
                <div
                  key={d.id}
                  onClick={() => { setDepSel(d.id); setVista('productos'); setQ(''); }}
                  style={{
                    borderRadius: 12, border: `1px solid ${activo ? COL.pri2 : COL.border}`,
                    background: COL.surface, padding: 12, cursor: 'pointer',
                    boxShadow: activo ? `0 0 0 1px ${COL.pri2}` : 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: activo ? COL.blueBg : COL.bg2, color: activo ? COL.pri2 : COL.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FiBox size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.nombre}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
                      <Badge COL={COL} tone="neutral" icon={FiHome}>{d.es_principal ? 'Principal' : d.codigo}</Badge>
                      {esVenta && <Badge COL={COL} tone="green" icon={FiShoppingCart}>Venta</Badge>}
                      {!d.activa && <Badge COL={COL} tone="red">Inactivo</Badge>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detalle del depósito seleccionado */}
          <div style={{ borderRadius: 14, border: `1px solid ${COL.border}`, background: COL.surface, overflow: 'hidden' }}>
            {!depSelInfo ? (
              <div style={{ padding: 40, textAlign: 'center', color: COL.muted, fontSize: 13 }}>
                Selecciona un depósito para ver su stock y ubicaciones.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${COL.border}`, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: COL.text }}>{depSelInfo.nombre}</div>
                    <div style={{ fontSize: 11.5, color: COL.muted, marginTop: 1 }}>
                      {detalle.productos.length} productos · {detalle.insumos.length} insumos · {detalle.ubicaciones.length} ubicaciones
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {puedeGestionar && (
                      <Boton COL={COL} ghost onClick={() => setModalDeposito({ editar: depSelInfo })}>
                        <FiEdit3 size={13} /> Editar
                      </Boton>
                    )}
                    {puedeGestionar && (
                      <IconBtn COL={COL} onClick={() => toggleDeposito(depSelInfo.id)} title={depSelInfo.activa ? 'Desactivar' : 'Activar'}>
                        {depSelInfo.activa ? <FiEyeOff size={15} /> : <FiCheck size={15} />}
                      </IconBtn>
                    )}
                    {puedeGestionar && !depSelInfo.es_principal && (
                      <IconBtn COL={COL} danger onClick={() => eliminarDeposito(depSelInfo.id)} title="Eliminar">
                        <FiTrash2 size={15} />
                      </IconBtn>
                    )}
                    {puedeGestionar && depoVenta?.id !== depSelInfo.id && (
                      <Boton COL={COL} ghost onClick={() => setDepositoVenta(depSelInfo.id)}>
                        <FiShoppingCart size={13} /> Usar para ventas
                      </Boton>
                    )}
                    {depoVenta?.id === depSelInfo.id && (
                      <Badge COL={COL} tone="green" icon={FiCheck}>Depósito de venta</Badge>
                    )}
                    {puedeTransferir && depositos.length > 1 && (
                      <Boton COL={COL} ghost onClick={() => setModalTransferir({ deposito_origen: depSelInfo.id })}>
                        <FiArrowRight size={13} /> Transferir
                      </Boton>
                    )}
                    {puedeGestionar && vista === 'productos' && (
                      <Boton COL={COL} ghost onClick={() => setModalUbicar({ tipo: 'productos' })}>
                        <FiMapPin size={13} /> Ubicar varios
                      </Boton>
                    )}
                    {puedeGestionar && vista === 'insumos' && (
                      <Boton COL={COL} ghost onClick={() => setModalUbicar({ tipo: 'insumos' })}>
                        <FiMapPin size={13} /> Ubicar varios
                      </Boton>
                    )}
                    {puedeGestionar && (
                      <Boton COL={COL} ghost onClick={() => setModalUbicacion({ id: null, nombre: '' })}>
                        <FiPlus size={13} /> Nueva ubicación
                      </Boton>
                    )}
                  </div>
                </div>

                {/* Sub-pestañas */}
                <div style={{ display: 'flex', gap: 4, padding: '0 16px', borderBottom: `1px solid ${COL.border}`, overflowX: 'auto' }}>
                  {[
                    { id: 'productos', label: 'Productos', show: vStock },
                    { id: 'insumos', label: 'Insumos', show: vStock },
                    { id: 'ubicaciones', label: 'Ubicaciones', show: vUbicacion },
                  ].filter((t) => t.show).map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setVista(t.id)}
                      style={{
                        padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                        fontSize: 13, fontWeight: vista === t.id ? 700 : 500,
                        color: vista === t.id ? COL.pri2 : COL.muted,
                        borderBottom: vista === t.id ? `2px solid ${COL.pri2}` : '2px solid transparent',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Buscador */}
                {vista !== 'ubicaciones' && (
                  <div style={{ padding: '12px 16px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: COL.bg2, borderRadius: 8, padding: '0 10px' }}>
                      <FiSearch size={14} style={{ color: COL.faint }} />
                      <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar por nombre o código…"
                        style={{ flex: 1, padding: '9px 0', border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: COL.text }}
                      />
                      {q && (
                        <button onClick={() => setQ('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.faint }}>
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ padding: '12px 16px 20px' }}>
                  {cargandoDetalle ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner /></div>
                  ) : vista === 'ubicaciones' ? (
                    <UbicacionesView
                      ubicaciones={detalle.ubicaciones} productos={detalle.productos} insumos={detalle.insumos}
                      COL={COL} isMobile={isMobile} puedeGestionar={puedeGestionar}
                      onNueva={() => setModalUbicacion({ id: null, nombre: '' })}
                      onEditar={(u) => setModalUbicacion({ id: u.id, codigo: u.codigo, nombre: u.nombre })}
                      onEliminar={eliminarUbicacion}
                    />
                  ) : vista === 'insumos' ? (
                    <InsumosView
                      insumos={insumosFiltrados} ubicaciones={detalle.ubicaciones} COL={COL} isMobile={isMobile}
                      puedeGestionar={puedeGestionar} vUbicacion={vUbicacion} onUbicar={ubicarInsumo}
                    />
                  ) : (
                    <ProductosView
                      productos={productosFiltrados} ubicaciones={detalle.ubicaciones} COL={COL} isMobile={isMobile}
                      puedeGestionar={puedeGestionar} vUbicacion={vUbicacion} onUbicar={ubicarProducto}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {modalDeposito && (
        <ModalDeposito
          COL={COL} isMobile={isMobile} editar={modalDeposito.editar}
          onClose={() => setModalDeposito(null)} onSave={guardarDeposito} guardando={guardando}
        />
      )}
      {modalUbicacion && (
        <ModalUbicacion
          COL={COL} isMobile={isMobile} editar={modalUbicacion}
          onClose={() => setModalUbicacion(null)} onSave={guardarUbicacion} guardando={guardando}
        />
      )}
      {modalTransferir && (
        <ModalTransferir
          COL={COL} isMobile={isMobile} depositos={depositos} origen={modalTransferir.deposito_origen}
          productos={detalle.productos}
          onClose={() => setModalTransferir(null)} onSave={transferir} guardando={guardando}
        />
      )}
      {modalUbicar && (
        <ModalUbicar
          COL={COL} isMobile={isMobile} ubicaciones={detalle.ubicaciones} tipo={modalUbicar.tipo}
          productos={detalle.productos} insumos={detalle.insumos}
          onClose={() => setModalUbicar(null)} onSave={modalUbicar.tipo === 'insumos' ? ubicarInsumos : ubicarProductos} guardando={guardando}
        />
      )}    </div>
  );
}

/* ──────────────────────────── Vistas de stock ──────────────────────────── */

function ProductosView({ productos, ubicaciones, COL, isMobile, puedeGestionar, vUbicacion, onUbicar }) {
  if (productos.length === 0) {
    return <MiniEmpty COL={COL} icon={FiPackage} text="Sin productos con stock en este depósito." />;
  }
  const sorted = [...productos].sort(
    (a, b) => (a.activo === false ? 1 : 0) - (b.activo === false ? 1 : 0) || (a.nombre || '').localeCompare(b.nombre || '')
  );
  const ubicacionNombre = (id) => {
    const u = ubicaciones.find((x) => x.id === id);
    return u ? u.nombre : null;
  };
  const SelectorUbi = ({ codigo, ubicacionId }) => (
    vUbicacion && puedeGestionar ? (
      <select
        value={ubicacionId || ''}
        onChange={(e) => onUbicar(codigo, e.target.value ? Number(e.target.value) : null)}
        style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg, color: COL.text, fontSize: 12, maxWidth: 160 }}
      >
        <option value="">Sin ubicación</option>
        {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
      </select>
    ) : (
      <span style={{ fontSize: 12, color: COL.muted }}>{ubicacionNombre(ubicacionId) || '—'}</span>
    )
  );

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map((p) => (
          <div key={p.codigo} style={{ border: `1px solid ${COL.border}`, borderRadius: 10, padding: 12, display: 'flex', gap: 10, opacity: p.activo === false ? 0.6 : 1 }}>
            <MiniImagen thumb={p.thumb_url} imagen={p.imagen_url} COL={COL} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 13.5, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre || p.codigo}</span>
                    {p.es_compuesto && (
                      <Badge COL={COL} tone="blue" icon={FiLayers}>Compuesto</Badge>
                    )}
                    {p.activo === false && (
                      <Badge COL={COL} tone="red">Inactivo</Badge>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: COL.faint }}>{p.codigo}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 10.5, color: COL.muted }}>Stock</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: p.disponible < 0 ? COL.red : COL.text }}>{num(p.cantidad)}</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8 }}>
                <span style={{ fontSize: 11.5, color: COL.muted }}>Disponible <strong style={{ color: COL.text }}>{num(p.disponible)}</strong></span>
                <SelectorUbi codigo={p.codigo} ubicacionId={p.ubicacion_id} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Tabla>
      <thead>
        <tr>
          <th style={TH}></th>
          <th style={TH}>Producto</th>
          <th style={TH}>Stock</th>
          <th style={TH}>Comprometido</th>
          <th style={TH}>Disponible</th>
          {vUbicacion && <th style={TH}>Ubicación</th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map((p) => (
          <tr key={p.codigo} style={{ borderBottom: `1px solid ${COL.border}`, opacity: p.activo === false ? 0.55 : 1 }}>
            <td style={{ ...TD, width: 44 }}><MiniImagen thumb={p.thumb_url} imagen={p.imagen_url} COL={COL} size={36} /></td>
            <td style={TD}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: COL.text }}>{p.nombre || p.codigo}</span>
                {p.es_compuesto && (
                  <Badge COL={COL} tone="blue" icon={FiLayers}>Compuesto</Badge>
                )}
                {p.activo === false && (
                  <Badge COL={COL} tone="red">Inactivo</Badge>
                )}
              </div>
              <div style={{ fontSize: 11, color: COL.faint }}>{p.codigo}</div>
            </td>
            <td style={TD}>{num(p.cantidad)}</td>
            <td style={TD}>{num(p.comprometida)}</td>
            <td style={{ ...TD, fontWeight: 700, color: p.disponible < 0 ? COL.red : COL.text }}>{num(p.disponible)}</td>
            {vUbicacion && <td style={TD}><SelectorUbi codigo={p.codigo} ubicacionId={p.ubicacion_id} /></td>}
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}

function InsumosView({ insumos, ubicaciones, COL, isMobile, puedeGestionar, vUbicacion, onUbicar }) {
  if (insumos.length === 0) {
    return <MiniEmpty COL={COL} icon={FiLayers} text="Sin insumos con stock en este depósito." />;
  }
  const sorted = [...insumos].sort(
    (a, b) => (a.activo === false ? 1 : 0) - (b.activo === false ? 1 : 0) || (a.nombre || '').localeCompare(b.nombre || '')
  );
  const ubicacionNombre = (id) => {
    const u = ubicaciones.find((x) => x.id === id);
    return u ? u.nombre : null;
  };
  const SelectorUbi = ({ insumoId, ubicacionId }) => (
    vUbicacion && puedeGestionar ? (
      <select
        value={ubicacionId || ''}
        onChange={(e) => onUbicar(insumoId, e.target.value ? Number(e.target.value) : null)}
        style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg, color: COL.text, fontSize: 12, maxWidth: 160 }}
      >
        <option value="">Sin ubicación</option>
        {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
      </select>
    ) : (
      <span style={{ fontSize: 12, color: COL.muted }}>{ubicacionNombre(ubicacionId) || '—'}</span>
    )
  );

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map((i) => (
          <div key={i.insumo_id} style={{ border: `1px solid ${COL.border}`, borderRadius: 10, padding: 12, opacity: i.activo === false ? 0.6 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.nombre || i.codigo}</span>
                  {i.activo === false && (
                    <Badge COL={COL} tone="red">Inactivo</Badge>
                  )}
                </div>
                <div style={{ fontSize: 11, color: COL.faint }}>{i.codigo} · {i.tipo}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 10.5, color: COL.muted }}>Stock</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: COL.text }}>{num(i.stock)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <SelectorUbi insumoId={i.insumo_id} ubicacionId={i.ubicacion_id} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Tabla>
      <thead>
        <tr>
          <th style={TH}>Insumo</th>
          <th style={TH}>Tipo</th>
          <th style={TH}>Stock</th>
          {vUbicacion && <th style={TH}>Ubicación</th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map((i) => (
          <tr key={i.insumo_id} style={{ borderBottom: `1px solid ${COL.border}`, opacity: i.activo === false ? 0.55 : 1 }}>
            <td style={TD}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: COL.text }}>{i.nombre || i.codigo}</span>
                {i.activo === false && (
                  <Badge COL={COL} tone="red">Inactivo</Badge>
                )}
              </div>
              <div style={{ fontSize: 11, color: COL.faint }}>{i.codigo}</div>
            </td>
            <td style={TD}>{i.tipo}</td>
            <td style={{ ...TD, fontWeight: 700 }}>{num(i.stock)}</td>
            {vUbicacion && <td style={TD}><SelectorUbi insumoId={i.insumo_id} ubicacionId={i.ubicacion_id} /></td>}
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}

function UbicacionesView({ ubicaciones, productos, insumos, COL, isMobile, puedeGestionar, onNueva, onEditar, onEliminar }) {
  if (ubicaciones.length === 0) {
    return (
      <MiniEmpty
        COL={COL} icon={FiMapPin}
        text="Aún no hay ubicaciones. Crea zonas (estantes, cavas, filas) para indicar dónde está cada producto."
        action={puedeGestionar ? { label: 'Crear ubicación', fn: onNueva } : null}
      />
    );
  }
  const contar = (id) => {
    const prod = productos.filter((p) => p.ubicacion_id === id).length;
    const ins = insumos.filter((i) => i.ubicacion_id === id).length;
    return { prod, ins };
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
      {ubicaciones.map((u) => {
        const c = contar(u.id);
        return (
          <div key={u.id} style={{ border: `1px solid ${COL.border}`, borderRadius: 12, padding: 14, background: COL.surface }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FiMapPin size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nombre}</div>
                <div style={{ fontSize: 11, color: COL.faint, fontFamily: 'monospace' }}>{u.codigo}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 8, fontSize: 11.5, color: COL.muted }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FiPackage size={12} /> {c.prod}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FiLayers size={12} /> {c.ins}</span>
              </div>
              {puedeGestionar && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <IconBtn COL={COL} onClick={() => onEditar(u)} title="Editar"><FiEdit2 size={13} /></IconBtn>
                  <IconBtn COL={COL} danger onClick={() => onEliminar(u.id)} title="Eliminar"><FiTrash2 size={13} /></IconBtn>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {puedeGestionar && (
        <button
          onClick={onNueva}
          style={{ border: `1px dashed ${COL.border}`, borderRadius: 12, padding: 14, background: 'transparent', cursor: 'pointer', color: COL.pri2, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 76 }}
        >
          <FiPlus size={14} /> Nueva ubicación
        </button>
      )}
    </div>
  );
}

/* ──────────────────────────── Modales ──────────────────────────── */

function ModalDeposito({ COL, isMobile, editar, onClose, onSave, guardando }) {
  const [nombre, setNombre] = useState(editar?.nombre || '');
  return (
    <Modal COL={COL} isMobile={isMobile} onClose={onClose}>
      <ModalHeader COL={COL} icon={FiBox} title={editar ? 'Editar depósito' : 'Nuevo depósito'} subtitle={editar ? `Modifica el nombre de "${editar.nombre}"` : 'Crea un depósito para esta sede (ej. Almacén, Cava, Bodega).'} onClose={onClose} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <FieldLabel COL={COL}>Nombre del depósito</FieldLabel>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus placeholder="Ej. Almacén central" style={inputStyle(COL)} />
      </div>
      <ModalFooter COL={COL} onClose={onClose} onSave={() => onSave(nombre, editar?.id)} label="Guardar" guardando={guardando} />
    </Modal>
  );
}

function ModalUbicacion({ COL, isMobile, editar, onClose, onSave, guardando }) {
  const [nombre, setNombre] = useState(editar?.nombre || '');
  const esEdicion = !!editar?.id;
  return (
    <Modal COL={COL} isMobile={isMobile} onClose={onClose}>
      <ModalHeader COL={COL} icon={FiMapPin} title={esEdicion ? 'Editar ubicación' : 'Nueva ubicación'} subtitle={esEdicion ? 'Modifica el nombre de esta zona. El código se genera automáticamente.' : 'Una zona dentro del depósito para ubicar productos. El código se genera automáticamente.'} onClose={onClose} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {esEdicion && editar?.codigo && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <FieldLabel COL={COL}>Código</FieldLabel>
            <div style={{ padding: '9px 11px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.bg2, fontSize: 13, color: COL.muted, fontFamily: 'monospace' }}>{editar.codigo}</div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <FieldLabel COL={COL}>Nombre</FieldLabel>
          <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus placeholder="Ej. Estante A, fila 1" style={inputStyle(COL)} />
        </div>
      </div>
      <ModalFooter COL={COL} onClose={onClose} onSave={() => onSave({ id: editar?.id || null, nombre })} label={esEdicion ? 'Guardar' : 'Crear'} guardando={guardando} />
    </Modal>
  );
}

function ModalUbicar({ COL, isMobile, ubicaciones, tipo, productos, insumos, onClose, onSave, guardando }) {
  const [ubicacionId, setUbicacionId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);

  const esInsumos = tipo === 'insumos';
  const origen = esInsumos ? insumos : productos;

  const keyOf = useCallback((x) => (esInsumos ? x.insumo_id : x.codigo), [esInsumos]);
  const texto = esInsumos ? 'insumo' : 'producto';
  const textos = esInsumos ? 'insumos' : 'productos';

  const disponibles = useMemo(() => {
    const enSeleccion = new Set(seleccionados.map((s) => s.key));
    const t = busqueda.trim().toLowerCase();
    const filtrados = origen.filter((x) => {
      if (enSeleccion.has(keyOf(x))) return false;
      if (t && !((x.nombre || '').toLowerCase().includes(t) || (x.codigo || '').toLowerCase().includes(t))) return false;
      return true;
    });
    return filtrados.sort((a, b) => Number(!!a.activo) - Number(!!b.activo) || (a.nombre || '').localeCompare(b.nombre || ''));
  }, [origen, busqueda, seleccionados, keyOf]);

  const agregar = (x) => {
    const k = keyOf(x);
    if (seleccionados.some((s) => s.key === k)) return;
    setSeleccionados((prev) => [...prev, { key: k, codigo: x.codigo, nombre: x.nombre || x.codigo, imagen_url: x.imagen_url, thumb_url: x.thumb_url, ubicacion_id: x.ubicacion_id }]);
    setBusqueda('');
  };

  const quitar = (k) => setSeleccionados((prev) => prev.filter((s) => s.key !== k));

  const ubicacionActual = (id) => {
    const u = ubicaciones.find((x) => x.id === id);
    return u ? u.nombre : null;
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: COL.overlay, zIndex: 3000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COL.surface, borderRadius: 16, width: '100%', maxWidth: 680,
          maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 24px 70px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${COL.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {esInsumos ? <FiLayers size={20} /> : <FiMapPin size={20} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16.5, fontWeight: 700, color: COL.text }}>Asignar ubicación</div>
              <div style={{ fontSize: 12.5, color: COL.muted, marginTop: 2, lineHeight: 1.4 }}>
                Ubica varios {textos} a la vez en una zona de este depósito.
              </div>
            </div>
            <IconBtn COL={COL} onClick={onClose}><FiX size={16} /></IconBtn>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Ubicación destino */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <FieldLabel COL={COL}>Ubicación</FieldLabel>
            <select value={ubicacionId} onChange={(e) => setUbicacionId(e.target.value ? Number(e.target.value) : '')} style={selectStyle(COL)}>
              <option value="">Sin ubicación (quitar)</option>
              {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </div>

          {/* Buscador */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <FieldLabel COL={COL}>Agregar {textos}</FieldLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: COL.bg2, borderRadius: 10, padding: '0 12px', border: `1px solid ${COL.border}` }}>
              <FiSearch size={15} style={{ color: COL.faint }} />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre o código…"
                style={{ flex: 1, padding: '10px 0', border: 'none', background: 'transparent', outline: 'none', fontSize: 13.5, color: COL.text }}
              />
              {busqueda && <button onClick={() => setBusqueda('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.faint }}><FiX size={14} /></button>}
            </div>
            {disponibles.length > 0 && (
              <div style={{ border: `1px solid ${COL.border}`, borderRadius: 10, overflow: 'hidden', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
                {disponibles.map((x) => {
                  const k = keyOf(x);
                  return (
                    <button
                      key={k}
                      onClick={() => agregar(x)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: 9, width: '100%',
                        border: 'none', borderBottom: `1px solid ${COL.border}`, background: x.activo === false ? COL.bg2 : COL.surface,
                        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      }}
                    >
                      {esInsumos
                        ? <MiniImagen thumb={null} imagen={null} COL={COL} size={38} />
                        : <MiniImagen thumb={x.thumb_url} imagen={x.imagen_url} COL={COL} size={38} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.nombre || x.codigo}</span>
                          {x.activo === false && <Badge COL={COL} tone="red">Inactivo</Badge>}
                        </div>
                        <div style={{ fontSize: 11, color: COL.faint }}>{x.codigo}</div>
                      </div>
                      {x.ubicacion_id && (
                        <span style={{ fontSize: 11, color: COL.muted, flexShrink: 0 }}>{ubicacionActual(x.ubicacion_id)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Lista de seleccionados */}
          {seleccionados.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {textos} seleccionados ({seleccionados.length})
              </div>
              {seleccionados.map((s) => (
                <div key={s.key} style={{ border: `1px solid ${COL.border}`, borderRadius: 12, padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                  {esInsumos
                    ? <MiniImagen thumb={null} imagen={null} COL={COL} size={46} />
                    : <MiniImagen thumb={s.thumb_url} imagen={s.imagen_url} COL={COL} size={46} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.nombre}</div>
                    <div style={{ fontSize: 11.5, color: COL.faint }}>{s.codigo}</div>
                    {s.ubicacion_id && (
                      <div style={{ fontSize: 11.5, color: COL.muted, marginTop: 3 }}>
                        Actual: {ubicacionActual(s.ubicacion_id)}
                      </div>
                    )}
                  </div>
                  <button onClick={() => quitar(s.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.faint, padding: 6 }}>
                    <FiTrash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {seleccionados.length === 0 && (
            <div style={{ textAlign: 'center', padding: '18px 0', color: COL.muted, fontSize: 13 }}>
              Busca y agrega {textos} para asignarles ubicación.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${COL.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12.5, color: COL.muted }}>
            {seleccionados.length > 0 ? `${seleccionados.length} ${texto}(s) seleccionado(s)` : `Sin ${textos} seleccionados`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Boton COL={COL} ghost onClick={onClose} disabled={guardando}>Cancelar</Boton>
            <Boton
              COL={COL}
              onClick={() => onSave(ubicacionId === '' ? null : ubicacionId, seleccionados.map((s) => s.key))}
              disabled={guardando || seleccionados.length === 0}
            >
              {guardando ? 'Guardando…' : 'Asignar ubicación'}
            </Boton>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalTransferir({ COL, isMobile, depositos, origen, productos, onClose, onSave, guardando }) {
  const [destino, setDestino] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);

  const destinos = depositos.filter((d) => d.id !== origen);

  const disponibles = useMemo(() => {
    const enSeleccion = new Set(seleccionados.map((s) => s.codigo));
    const t = busqueda.trim().toLowerCase();
    return productos.filter((p) => {
      if (enSeleccion.has(p.codigo)) return false;
      if (t && !((p.nombre || '').toLowerCase().includes(t) || (p.codigo || '').toLowerCase().includes(t))) return false;
      return true;
    });
  }, [productos, busqueda, seleccionados]);

  const agregar = (p) => {
    if (seleccionados.some((s) => s.codigo === p.codigo)) return;
    const disp = Number(p.disponible || 0);
    setSeleccionados((prev) => [
      ...prev,
      { codigo: p.codigo, nombre: p.nombre || p.codigo, disponible: disp, cantidad: disp > 0 ? 1 : '', imagen_url: p.imagen_url, thumb_url: p.thumb_url },
    ]);
    setBusqueda('');
  };

  const quitar = (codigo) => setSeleccionados((prev) => prev.filter((s) => s.codigo !== codigo));

  const setCantidad = (codigo, val) => {
    setSeleccionados((prev) =>
      prev.map((s) => {
        if (s.codigo !== codigo) return s;
        if (val === '' || val == null) return { ...s, cantidad: '' };
        let n = Number(val);
        if (Number.isNaN(n)) n = 0;
        n = Math.min(Math.max(n, 0), s.disponible);
        return { ...s, cantidad: n };
      })
    );
  };

  const step = (codigo, delta) => {
    setSeleccionados((prev) =>
      prev.map((s) => {
        if (s.codigo !== codigo) return s;
        const base = Number(s.cantidad || 0);
        const n = Math.min(Math.max(base + delta, 0), s.disponible);
        return { ...s, cantidad: n };
      })
    );
  };

  const totalUnidades = seleccionados.reduce((a, s) => a + Number(s.cantidad || 0), 0);
  const listos = seleccionados.filter((s) => Number(s.cantidad) > 0);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: COL.overlay, zIndex: 3000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COL.surface, borderRadius: 16, width: '100%', maxWidth: 680,
          maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 24px 70px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${COL.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FiArrowRight size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16.5, fontWeight: 700, color: COL.text }}>Transferir stock</div>
              <div style={{ fontSize: 12.5, color: COL.muted, marginTop: 2, lineHeight: 1.4 }}>
                Mueve varios productos a la vez a otro depósito de la misma sede.
              </div>
            </div>
            <IconBtn COL={COL} onClick={onClose}><FiX size={16} /></IconBtn>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Destino */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <FieldLabel COL={COL}>Depósito destino</FieldLabel>
            <select value={destino} onChange={(e) => setDestino(Number(e.target.value))} style={selectStyle(COL)}>
              <option value="">Selecciona…</option>
              {destinos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          </div>

          {/* Buscador */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <FieldLabel COL={COL}>Agregar productos</FieldLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: COL.bg2, borderRadius: 10, padding: '0 12px', border: `1px solid ${COL.border}` }}>
              <FiSearch size={15} style={{ color: COL.faint }} />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre o código…"
                style={{ flex: 1, padding: '10px 0', border: 'none', background: 'transparent', outline: 'none', fontSize: 13.5, color: COL.text }}
              />
              {busqueda && <button onClick={() => setBusqueda('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.faint }}><FiX size={14} /></button>}
            </div>
            {disponibles.length > 0 && (
              <div style={{ border: `1px solid ${COL.border}`, borderRadius: 10, overflow: 'hidden', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
                {disponibles.map((p) => {
                  const disp = Number(p.disponible || 0);
                  const sin = disp <= 0;
                  return (
                    <button
                      key={p.codigo}
                      onClick={() => !sin && agregar(p)}
                      disabled={sin}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: 9, width: '100%',
                        border: 'none', borderBottom: `1px solid ${COL.border}`, background: sin ? COL.bg2 : COL.surface,
                        cursor: sin ? 'not-allowed' : 'pointer', textAlign: 'left', fontFamily: 'inherit', opacity: sin ? 0.55 : 1,
                      }}
                    >
                      <MiniImagen thumb={p.thumb_url} imagen={p.imagen_url} COL={COL} size={38} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre || p.codigo}</div>
                        <div style={{ fontSize: 11, color: COL.faint }}>{p.codigo}</div>
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: sin ? COL.red : COL.muted, flexShrink: 0 }}>
                        {sin ? 'Sin stock' : `Disp. ${num(disp)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Lista de seleccionados */}
          {seleccionados.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Productos a transferir ({seleccionados.length})
              </div>
              {seleccionados.map((s) => {
                const disp = Number(s.disponible || 0);
                const cant = Number(s.cantidad || 0);
                return (
                  <div key={s.codigo} style={{ border: `1px solid ${COL.border}`, borderRadius: 12, padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <MiniImagen thumb={s.thumb_url} imagen={s.imagen_url} COL={COL} size={46} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: COL.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.nombre}</div>
                      <div style={{ fontSize: 11.5, color: COL.faint }}>{s.codigo}</div>
                      <div style={{ fontSize: 11.5, color: COL.muted, marginTop: 3 }}>
                        Disponible: <strong style={{ color: COL.text }}>{num(disp)}</strong>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <StepperBtn COL={COL} onClick={() => step(s.codigo, -1)} disabled={cant <= 0}>−</StepperBtn>
                      <input
                        value={s.cantidad}
                        type="number"
                        min="0"
                        max={disp}
                        step="any"
                        onChange={(e) => setCantidad(s.codigo, e.target.value)}
                        onBlur={(e) => { if (e.target.value === '' ) setCantidad(s.codigo, 0); }}
                        style={{
                          width: 62, padding: '7px 6px', textAlign: 'center', borderRadius: 8,
                          border: `1px solid ${COL.border}`, background: COL.inputBg, color: COL.text,
                          fontSize: 14, fontWeight: 700, fontFamily: 'inherit', outline: 'none',
                        }}
                      />
                      <StepperBtn COL={COL} onClick={() => step(s.codigo, 1)} disabled={cant >= disp}>+</StepperBtn>
                      <button onClick={() => quitar(s.codigo)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.faint, padding: 6 }}>
                        <FiTrash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {seleccionados.length === 0 && (
            <div style={{ textAlign: 'center', padding: '18px 0', color: COL.muted, fontSize: 13 }}>
              Busca y agrega productos para transferir.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${COL.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12.5, color: COL.muted }}>
            {listos.length > 0
              ? `${listos.length} producto(s) · ${num(totalUnidades)} unidad(es)`
              : 'Sin productos seleccionados'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Boton COL={COL} ghost onClick={onClose} disabled={guardando}>Cancelar</Boton>
            <Boton
              COL={COL}
              onClick={() => onSave(destino, listos.map((s) => ({ codigo: s.codigo, nombre: s.nombre, cantidad: Number(s.cantidad) })))}
              disabled={guardando || !destino || listos.length === 0}
            >
              {guardando ? 'Transfiriendo…' : 'Transferir'}
            </Boton>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepperBtn({ COL, onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 30, height: 30, borderRadius: 8, border: `1px solid ${COL.border}`,
        background: COL.bg2, color: COL.text, cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: disabled ? 0.4 : 1, fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

/* ──────────────────────────── Primitivas UI ──────────────────────────── */

function Boton({ COL, ghost, onClick, children, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 13px',
        borderRadius: 8, border: ghost ? `1px solid ${COL.border}` : 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: ghost ? 'transparent' : COL.pri2, color: ghost ? COL.text : '#fff',
        fontSize: 12.5, fontWeight: 600, minHeight: 36, opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function IconBtn({ COL, danger, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 6, border: 'none', cursor: 'pointer', background: danger ? COL.redBg : COL.bg2, color: danger ? COL.red : COL.text }}>
      {children}
    </button>
  );
}

function Badge({ COL, tone = 'neutral', icon: Icon, children }) {
  const tones = {
    neutral: { bg: COL.bg2, color: COL.muted },
    green: { bg: COL.greenBg, color: COL.green },
    red: { bg: COL.redBg, color: COL.red },
    blue: { bg: COL.blueBg, color: COL.pri2 },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: t.bg, color: t.color, whiteSpace: 'nowrap' }}>
      {Icon && <Icon size={10} />}
      {children}
    </span>
  );
}

function Tabla({ children }) {
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 480 }}>{children}</table>
    </div>
  );
}

const TH = { textAlign: 'left', padding: '9px 10px', fontSize: 11, fontWeight: 700, color: 'var(--agx-muted)', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--agx-border)' };
const TD = { padding: '9px 10px', fontSize: 13, color: 'var(--agx-text)' };

function inputStyle(COL) {
  return { boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg, color: COL.text, fontSize: 13, width: '100%', minWidth: 0, outline: 'none' };
}
function selectStyle(COL) {
  return { boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13, width: '100%', minWidth: 0 };
}

function FieldLabel({ COL, children }) {
  return <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>{children}</span>;
}

function Modal({ COL, isMobile, onClose, children, wide }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: COL.overlay, zIndex: 3000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: COL.surface, borderRadius: isMobile ? '16px 16px 0 0' : 14, width: '100%', maxWidth: wide ? 560 : 480, maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        {children}
      </div>
    </div>
  );
}

function MiniImagen({ thumb, imagen, COL, size = 44 }) {
  const src = getImagenUrl(thumb || imagen);
  return (
    <div style={{ width: size, height: size, borderRadius: 8, overflow: 'hidden', background: COL.bg2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {src ? (
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <FiPackage size={Math.round(size * 0.5)} style={{ color: COL.faint }} />
      )}
    </div>
  );
}

function ModalHeader({ COL, icon: Icon, title, subtitle, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 18px 4px' }}>
      {Icon && (
        <div style={{ width: 38, height: 38, borderRadius: 10, background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={19} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COL.text }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12.5, color: COL.muted, marginTop: 2, lineHeight: 1.45 }}>{subtitle}</div>}
      </div>
      <IconBtn COL={COL} onClick={onClose}><FiX size={16} /></IconBtn>
    </div>
  );
}

function ModalFooter({ COL, onClose, onSave, label, guardando }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 16, position: 'sticky', bottom: 0, background: COL.surface, borderTop: `1px solid ${COL.border}` }}>
      <Boton COL={COL} ghost onClick={onClose} disabled={guardando}>Cancelar</Boton>
      <Boton COL={COL} onClick={onSave} disabled={guardando}>{guardando ? 'Guardando…' : (label || 'Guardar')}</Boton>
    </div>
  );
}

function EmptyPanel({ COL, icon: Icon, title, desc, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 20px', background: COL.surface, borderRadius: 14, border: `1px dashed ${COL.border}` }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
        <Icon size={26} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: COL.text }}>{title}</div>
      <div style={{ fontSize: 13, color: COL.muted, marginTop: 6, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.55 }}>{desc}</div>
      {action && (
        <div style={{ marginTop: 16 }}>
          <Boton COL={COL} onClick={action.fn}><FiPlus size={15} /> {action.label}</Boton>
        </div>
      )}
    </div>
  );
}

function MiniEmpty({ COL, icon: Icon, text, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '28px 16px', color: COL.muted }}>
      <Icon size={28} style={{ color: COL.faint, marginBottom: 8 }} />
      <div style={{ fontSize: 13, maxWidth: 320, margin: '0 auto', lineHeight: 1.5 }}>{text}</div>
      {action && (
        <div style={{ marginTop: 12 }}>
          <Boton COL={COL} ghost onClick={action.fn}><FiPlus size={14} /> {action.label}</Boton>
        </div>
      )}
    </div>
  );
}
